export type ServicePriceType = 'net' | 'gross'

export interface AdditionalServiceInput {
  name: string
  amount: string
  price_type: ServicePriceType
  vat_option: string
  delivery_cost: boolean
}

export interface AdditionalServicePayload {
  name: string
  net_price: number
  vat_option: string
  delivery_cost: boolean
}

const serviceVatRates: Record<string, number> = {
  vat_23: 23,
  vat_10: 10,
  vat_5: 5
}

export const additionalServicesFromInitial = (
  services: AdditionalServiceInput[] | undefined
): AdditionalServiceInput[] => {
  return (services ?? []).map((service) => ({
    name: service.name ?? '',
    amount: String(service.amount ?? ''),
    price_type: service.price_type === 'net' ? 'net' : 'gross',
    vat_option: service.vat_option ?? '',
    delivery_cost: Boolean(service.delivery_cost)
  }))
}

const roundMoney = (value: number): number => Math.round(value * 100) / 100

const serviceAmount = (amount: string): number => {
  const parsed = Number(amount.replace(',', '.'))
  return Number.isNaN(parsed) ? 0 : parsed
}

export const additionalServiceNet = (service: AdditionalServiceInput): number => {
  const rate = serviceVatRates[service.vat_option] ?? 0
  const amount = serviceAmount(service.amount)
  if (service.price_type === 'gross') {
    return roundMoney(amount / (1 + rate / 100))
  }
  return roundMoney(amount)
}

export const additionalServiceGross = (service: AdditionalServiceInput): number => {
  const rate = serviceVatRates[service.vat_option] ?? 0
  const amount = serviceAmount(service.amount)
  if (service.price_type === 'net') {
    return roundMoney(amount * (1 + rate / 100))
  }
  return roundMoney(amount)
}

export interface AdditionalServiceBreakdown {
  netPrice: number
  grossPrice: number
  vatAmount: number
  vatRate: number
}

export const additionalServiceBreakdown = (service: AdditionalServiceInput): AdditionalServiceBreakdown => {
  const netPrice = additionalServiceNet(service)
  const grossPrice = additionalServiceGross(service)
  return {
    netPrice,
    grossPrice,
    vatAmount: roundMoney(grossPrice - netPrice),
    vatRate: serviceVatRates[service.vat_option] ?? 0
  }
}

export const prepareAdditionalServices = (services: AdditionalServiceInput[]): AdditionalServicePayload[] => {
  let deliveryCostTaken = false
  return services
    .filter((service) => service.name.trim().length > 0 || service.amount.trim().length > 0)
    .map((service) => {
      const deliveryCost = service.delivery_cost && !deliveryCostTaken
      if (deliveryCost) {
        deliveryCostTaken = true
      }
      return {
        name: service.name.trim(),
        net_price: additionalServiceNet(service),
        vat_option: service.vat_option,
        delivery_cost: deliveryCost
      }
    })
}

export const additionalServicesInvalid = (services: AdditionalServiceInput[]): boolean => {
  return services.some((service) => {
    const name = service.name.trim()
    const amount = service.amount.trim()
    if (name.length === 0 && amount.length === 0) {
      return false
    }
    const parsedAmount = Number(amount)
    if (name.length === 0 || amount.length === 0 || Number.isNaN(parsedAmount) || parsedAmount <= 0) {
      return true
    }
    return !(service.vat_option in serviceVatRates)
  })
}
